import $ from 'jquery';

import Asyncview from '@pixelunion/shopify-asyncview';
import morphdom from 'morphdom';
import rimg from '@pixelunion/rimg-shopify';

/**
 * _getItemKey
 *
 * Checks a target and recursively checks parents until an element with a data-cart-item-key
 * is found, when the value of that attribute is returned.
 *
 * @param {HTMLElement} target Element from which to start search for item key (inclusive)
 */
function _getItemKey(target) {
  if (!target) {
    return null;
  }

  if (target.hasAttribute('data-cart-item-key')) {
    return target.dataset.cartItemKey;
  }

  return _getItemKey(target.parentNode);
}

export default class StaticCart {
  constructor(section) {
    this.$el = $(section.el);

    this.events = [
      $('.cart-instructions textarea', this.$el).on('change.cart', () => this.saveSpecialInstructions()),
      $('[data-calculate-shipping-button]', this.$el).on('click.cart', () => this.calculateShipping()),
    ];

    this.cartForm = section.el.querySelector('[data-cart-form]');

    if (this.cartForm) {
      this.cartTable = this.cartForm.querySelector('[data-cart-table]');
      this.cartDiscounts = this.cartForm.querySelector('[data-cart-discounts]');
      this.totalPrice = this.cartForm.querySelector('[data-cart-subtotal]');

      this._handleClick = this._handleClick.bind(this);
      this._handleQtyChange = this._handleQtyChange.bind(this);
      this._handleQtyKeydown = this._handleQtyKeydown.bind(this);

      this.cartForm.addEventListener('click', this._handleClick);
      this.cartForm.addEventListener('change', this._handleQtyChange);
      this.cartForm.addEventListener('keydown', this._handleQtyKeydown);

      this.updateTimeout = null;
    }

    this.context = {};
    this.context.cart = JSON.parse($('[data-cart-strings]').text());
    this.context.shipping = null;

    this.$body = $(document.body);

    this.$shippingCalculator = $('[data-shipping-calculator]');

    if (this.$shippingCalculator.length !== 0) {
      try {
        this.context.shipping = JSON.parse($('[data-shipping-calculator-strings]').text());
      } catch (error) {
        console.log('No shipping localisations found, unable to continue.');
      }

      if (this.context.shipping == null) {
        return;
      }

      if (this.context.shipping.customerCountry) {
        this.calculateShipping();
      }

      if ($('.cart-items').length) {
        this.shippingCalculator();
      }
    }

    Shopify.onError = (XMLHttpRequest) => {
      this.handleErrors(XMLHttpRequest);
    };
  }

  saveSpecialInstructions() {
    const newNote = $('.cart-instructions textarea').val();
    Shopify.updateCartNote(newNote, (cart) => {
      // empty function so that Shopify's callback isn't invoked
    });
  }

  /**
   * Gets the current value of the quantity input box for a given line item key
   *
   * @param {string} key
   */
  _getItemQuantity(key) {
    return parseInt(
      this.cartForm
        .querySelector(`[data-cart-item-key="${key}"] [data-cart-product-quantity]`)
        .value,
      10,
    );
  }

  /**
   * Sets a value for the quantity input box for a given line item key
   *
   * @param {string} key
   *
   * @param {integer} quantity
   */
  _setItemQuantity(key, quantity) {
    this.cartForm
      .querySelector(`[data-cart-item-key="${key}"] [data-cart-product-quantity]`)
      .value = quantity;
  }

  _handleClick(event) {
    if (!event.target.hasAttribute('data-cart-action')) {
      return;
    }

    const { cartAction } = event.target.dataset;
    const key = _getItemKey(event.target);
    const oldQuantity = this._getItemQuantity(key);
    let newQuantity;

    switch (cartAction) {
      case 'increment':
        newQuantity = oldQuantity + 1;
        this._setItemQuantity(key, newQuantity);
        this._updateItem(key, newQuantity);
        break;

      case 'decrement':
        newQuantity = Math.max(oldQuantity - 1, 0);
        this._setItemQuantity(key, newQuantity);
        this._updateItem(key, newQuantity);
        break;

      case 'remove':
        event.preventDefault();
        this._setItemQuantity(key, 0);
        this._updateItem(key, 0);
        break;

      default:
        // do nothing
        break;
    }
  }

  _handleQtyChange(event) {
    if (!event.target.hasAttribute('data-cart-product-quantity')) {
      return;
    }

    const key = _getItemKey(event.target);
    const quantity = this._getItemQuantity(key);
    this._updateItem(key, quantity);
  }

  _handleQtyKeydown(event) {
    if (event.target.hasAttribute('data-cart-product-quantity') && event.key === 'Enter') {
      event.preventDefault();
      this._handleQtyChange(event);
    }
  }

  /**
   * Sends a request to the server to update a single product
   *
   * @param {string} key Shopify line item key (or product ID)
   *
   * @param {integer} quantity New quantity for item. If 0, item will be removed from cart.
   * @private
   */
  _updateItem(key, quantity) {
    // cancel any pending requests
    if (this.updateTimeout !== null) {
      clearTimeout(this.updateTimeout);
    }

    this.updateTimeout = setTimeout(() => {
      if (this._getItemQuantity(key) !== quantity) {
        this.updateTimeout = null;
        return;
      }

      const thisTimeoutId = this.updateTimeout;

      $.ajax({
        type: 'POST',
        url: `${window.Theme.routes.cart_change_url}.js`,
        dataType: 'json',
        data: {
          quantity,
          id: key,
        },
        success: (cart) => {
          if (this.updateTimeout !== thisTimeoutId) {
            return;
          }

          if (cart.item_count === 0) {
            // reload to show empty cart state
            window.location.reload();
            return;
          }

          this._updateCart(thisTimeoutId);
        },
      });
    }, 300);
  }

  /**
   * _updateCart
   *
   * Fetches new cart contents and swaps into page
   *
   * @param {integer} thisTimeoutId Id of timeout for this request. If no longer current, update is cancelled.
   */
  _updateCart(thisTimeoutId) {
    Asyncview.load(
      window.Theme.routes.cart_url,
      {
        view: '_ajax',
      },
    )
      .done(({ html, data }) => {
        // If another request is in progress, discard this update
        if (this.updateTimeout !== thisTimeoutId) {
          return;
        }

        // Inject new cart table contents
        const newTableContainer = document.createElement('div');
        newTableContainer.innerHTML = html.table;

        morphdom(
          this.cartTable,
          newTableContainer.querySelector('table'),
          {
            onBeforeElUpdated: (fromEl, toEl) => {
              // Skip images if src matches
              // - we don't want to reload lazy loaded images
              if (fromEl.tagName === 'IMG' && fromEl.src === toEl.src) {
                return false;
              }
              return true;
            },
          },
        );

        rimg.watch(this.cartTable);

        // Inject new cart level discounts
        this.cartDiscounts.innerHTML = html.discounts;

        // Update total
        this.totalPrice.innerHTML = data.totalPrice;
      })
      .fail(() => window.location.reload());
  }

  shippingCalculator() {
    Shopify.Cart.ShippingCalculator.show({
      submitButton: this.context.shipping.submitButton,
      submitButtonDisabled: this.context.shipping.submitButtonProcessing,
      customerIsLoggedIn: this.context.shipping.customerCountry,
      moneyFormat: Theme.moneyFormat,
    });

    const selectableOptions = this.$shippingCalculator.find('select');

    setTimeout(() => {
      selectableOptions.map(select => this.updateShippingLabel(select));
    }, 500);

    $('.cart-shipping-calculator select').change(() => {
      selectableOptions.map(select => this.updateShippingLabel(select));
    });
  }

  calculateShipping() {
    $('[data-calculate-shipping-button]').val(this.context.shipping.submitButtonProcessing);

    const shippingAddress = {};
    shippingAddress.zip = $('.address-zip').val() || '';
    shippingAddress.country = $('.address-country').val() || '';
    shippingAddress.province = $('.address-province').val() || '';

    const queryString = Object.keys(shippingAddress)
      .map(key => `${encodeURIComponent(`shipping_address[${key}]`)}=${encodeURIComponent(shippingAddress[key])}`)
      .join('&');

    $.ajax(`${window.Theme.routes.cart_url}/shipping_rates.json?${queryString}`, { dataType: 'json' })
      .fail(error => this.handleErrors(error.responseJSON || {}))
      .done(response => {
        const rates = response.shipping_rates;

        let address;
        let responseText;

        if (shippingAddress.zip.length) {
          address = shippingAddress.zip;
        }
        if (shippingAddress.province.length) {
          address = `${address}, ${shippingAddress.province}`;
        }
        if (shippingAddress.country.length) {
          address = `${address}, ${shippingAddress.country}`;
        }

        const shippingCalculatorResponse = $('.cart-shipping-calculator-response');
        shippingCalculatorResponse.empty().append("<p class='shipping-calculator-response message'/><ul class='shipping-rates'/>");

        const ratesFeedback = $('.shipping-calculator-response');

        if (rates.length > 1) {
          const firstRate = Shopify.Cart.ShippingCalculator.formatRate(rates[0].price);
          responseText = this.context.shipping.multiRates
            .replace('** address **', address)
            .replace('** number_of_rates **', rates.length)
            .replace('** rate **', `<span class='money'>${firstRate}</span>`);
          ratesFeedback.html(responseText);
        } else if (rates.length === 1) {
          responseText = this.context.shipping.oneRate
            .replace('** address **', address);
          ratesFeedback.html(responseText);
        } else {
          ratesFeedback.html(this.context.shipping.noRates);
        }

        rates.forEach(rate => {
          const price = Shopify.Cart.ShippingCalculator.formatRate(rate.price);
          const rateValues = this.context.shipping.rateValues
            .replace('** rate_title **', rate.name)
            .replace('** rate **', `<span class='money'>${price}</span>`);

          $('.shipping-rates').append(`<li>${rateValues}</li>`);
        });

        $('[data-calculate-shipping-button]').val(this.context.shipping.submitButton);
      });
  }

  updateShippingLabel(value) {
    if (value) {
      const select = $(value);
      let selectedOption = select.find('option:selected').val();

      if (!selectedOption) {
        selectedOption = select.prev('.selected-text').data('default');
      }

      select.prev('.selected-text').text(selectedOption);

      setTimeout(() => {
        if (select.attr('name') === 'address[country]') {
          this.updateShippingLabel($('#address_province'));
        }
      }, 500);
    }
  }

  handleErrors(errorMessage) {
    if (errorMessage.zip || errorMessage.country) {
      let errorText = '';

      if (errorMessage.country) {
        errorText += `${this.context.shipping.countryErrorMessage.replace('** error_message **', errorMessage.country)}<br/>`;
      }

      if (errorMessage.zip) {
        errorText += this.context.shipping.zipErrorMessage.replace('** error_message **', errorMessage.zip);
      }

      $('.cart-shipping-calculator-response').html(`<p>${errorText}</p>`);
    } else {
      console.log('Error', errorMessage.stringify());
    }
  }

  onSectionUnload() {
    this.events.forEach($el => $el.off('.cart'));

    if (this.cartForm) {
      this.cartForm.removeEventListener('click', this._handleClick);
      this.cartForm.removeEventListener('change', this._handleQtyChange);
      this.cartForm.removeEventListener('keydown', this._handleQtyKeydown);
    }
  }
}
